import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Client } from '@/types';
import { socialMediaApi, SocialMediaStatus } from '@/domains/Approval/services/socialMediaApi';
import { RefreshIcon, SpinnerIcon } from '@/components/icons';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

type AdminPage = {
  id: string;
  pageId: string;
  pageName: string;
  connectionStatus: string;
  instagramAccount: { id: string; username: string } | null;
};

type DisconnectTarget = "meta" | "tiktok";

const DISCONNECT_CONFIG: Record<DisconnectTarget, {
  title: string;
  message: (clientName: string) => string;
}> = {
  meta: {
    title: "Disconnect Facebook & Instagram?",
    message: (name) =>
      `This will remove all Facebook and Instagram connections for ${name}. They will need to reconnect to post content.`,
  },
  tiktok: {
    title: "Disconnect TikTok?",
    message: (name) =>
      `This will remove the TikTok connection for ${name}. They will need to reconnect to use TikTok features.`,
  },
};

interface SocialMediaSectionProps {
  client: Client;
}

export const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ client }) => {
  const [status, setStatus] = useState<SocialMediaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSavingAutopublish, setIsSavingAutopublish] = useState(false);
  const [isConnecting, setIsConnecting] = useState<DisconnectTarget | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<DisconnectTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pagePickerPages, setPagePickerPages] = useState<AdminPage[] | null>(null);
  const [isSelectingPage, setIsSelectingPage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchStatus();
  }, [client.id]);

  // Landing spot for admin-initiated OAuth rounds: the callback redirects
  // back here with the same query flags the portal pages use.
  useEffect(() => {
    const errorType = searchParams.get('type');
    const selectPage = searchParams.get('select_page');
    const anyFlag =
      errorType || selectPage ||
      searchParams.get('tiktok_connected') ||
      searchParams.get('facebook_connected');
    if (!anyFlag) return;

    if (errorType) {
      const message = searchParams.get('message');
      setError(message ? decodeURIComponent(message) : `Connection failed (${errorType})`);
    }
    if (selectPage === 'true') {
      openPagePicker();
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPagePicker = async () => {
    try {
      const result = await socialMediaApi.getAvailablePagesAdmin(client.id);
      setPagePickerPages(result.pages);
    } catch (err) {
      console.error('Failed to load Facebook pages:', err);
      setError('Failed to load Facebook pages');
    }
  };

  const handleSelectPage = async (facebookPageId: string) => {
    try {
      setIsSelectingPage(true);
      setError(null);
      await socialMediaApi.selectPageAdmin(client.id, facebookPageId);
      setPagePickerPages(null);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to select page:', err);
      setError('Failed to select Facebook page');
    } finally {
      setIsSelectingPage(false);
    }
  };

  const handleConnect = async (target: DisconnectTarget) => {
    try {
      setIsConnecting(target);
      setError(null);
      const result = target === 'tiktok'
        ? await socialMediaApi.connectTiktokAdmin(client.id)
        : await socialMediaApi.connectFacebookAdmin(client.id);
      window.location.href = result.oauthUrl;
    } catch (err) {
      console.error('Failed to initiate connection:', err);
      setError('Failed to start the connection flow');
      setIsConnecting(null);
    }
  };

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const data = await socialMediaApi.getAdminStatus(client.id);
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch social media status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;

    try {
      setIsDisconnecting(true);
      setError(null);

      const result = disconnectTarget === "tiktok"
        ? await socialMediaApi.disconnectTiktok(client.id)
        : await socialMediaApi.disconnect(client.id);

      if (result.success) {
        setDisconnectTarget(null);
        await fetchStatus();
      } else {
        const errorResult = result as { success: boolean; error?: string };
        setError(errorResult.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect social media accounts');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleValidate = async () => {
    try {
      setIsValidating(true);
      setError(null);

      const result = await socialMediaApi.validateConnection(client.id);

      if (!result.success) {
        setError(result.error || 'Failed to validate connection');
      }

      await fetchStatus();
    } catch (err) {
      console.error('Failed to validate connection:', err);
      setError('Failed to validate social media connection');
    } finally {
      setIsValidating(false);
    }
  };

  const handleToggleAutopublish = async () => {
    try {
      setIsSavingAutopublish(true);
      setError(null);
      await socialMediaApi.setTiktokAutopublish(
        client.id,
        !(status?.tiktok?.autopublishEnabled ?? false)
      );
      await fetchStatus();
    } catch (err) {
      console.error('Failed to update TikTok auto-publish:', err);
      setError('Failed to update TikTok auto-publish');
    } finally {
      setIsSavingAutopublish(false);
    }
  };

  const getTokenStatus = (expiresAtString: string | null | undefined) => {
    if (!expiresAtString) return { status: 'valid', label: 'Connected', color: 'green' };

    const expiresAt = new Date(expiresAtString);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiration <= 0) {
      return { status: 'expired', label: 'Expired', color: 'red' };
    } else if (daysUntilExpiration <= 7) {
      return { status: 'expiring', label: `Expiring in ${daysUntilExpiration} days`, color: 'yellow' };
    } else {
      return { status: 'valid', label: 'Connected', color: 'green' };
    }
  };

  if (isLoading) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media Accounts</h3>
        <div className="flex items-center justify-center py-8">
          <svg className="animate-spin h-6 w-6 text-blue-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  const facebookConnected = status?.facebook.connected || false;
  const instagramConnected = status?.instagram.connected || false;
  const tiktokConnected = status?.tiktok?.connected || false;
  const autopublishEnabled = status?.tiktok?.autopublishEnabled || false;
  const facebookPage = status?.facebook.pages[0];
  const instagramAccount = status?.instagram.accounts[0];
  const tiktokAccount = status?.tiktok?.accounts?.[0];

  const metaConnected = facebookConnected || instagramConnected;
  const tokenStatus = facebookPage ? getTokenStatus(facebookPage.expiresAt) : null;

  const confirmConfig = disconnectTarget ? DISCONNECT_CONFIG[disconnectTarget] : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Social Media Accounts</h3>
        {(metaConnected || tiktokConnected) && (
          <button
            onClick={handleValidate}
            disabled={isValidating}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshIcon className={`w-4 h-4 ${isValidating ? 'animate-spin' : ''}`} />
            {isValidating ? 'Checking...' : 'Refresh'}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {/* Meta (Facebook + Instagram) group */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Facebook & Instagram
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleConnect("meta")}
                disabled={isConnecting !== null}
                className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {isConnecting === "meta" && <SpinnerIcon className="w-3 h-3" />}
                {metaConnected ? "Reconnect" : "Connect"}
              </button>
              {metaConnected && (
                <button
                  onClick={() => setDisconnectTarget("meta")}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm font-medium transition-colors"
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>

          {/* Post-OAuth page picker: the callback lands back here with
              select_page=true when more than one page came through. */}
          {pagePickerPages !== null && (
            <div className="p-4 bg-blue-50 border-b border-blue-200">
              <div className="text-sm font-medium text-gray-900 mb-2">
                Select the Facebook Page to use for {client.clientName}
              </div>
              {pagePickerPages.length === 0 ? (
                <div className="text-sm text-gray-600">No pages found.</div>
              ) : (
                <div className="space-y-1">
                  {pagePickerPages.map((page) => (
                    <div
                      key={page.id}
                      className="flex items-center justify-between px-3 py-2 bg-white rounded-md border border-gray-200"
                    >
                      <div className="text-sm text-gray-900">
                        {page.pageName}
                        {page.instagramAccount && (
                          <span className="text-gray-500 ml-2">
                            · @{page.instagramAccount.username}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleSelectPage(page.id)}
                        disabled={isSelectingPage}
                        className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                      >
                        Use this page
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Facebook */}
          <div className="flex items-center justify-between p-4 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${facebookConnected ? 'bg-green-100' : 'bg-gray-200'}`}>
                {facebookConnected ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-sm font-bold text-gray-500">f</span>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">Facebook</div>
                {facebookConnected && facebookPage ? (
                  <div className="flex flex-col">
                    <div className="text-sm text-gray-600">{facebookPage.pageName}</div>
                    {facebookPage.expiresAt && (
                      <div className="text-xs text-gray-400">
                        Expires: {new Date(facebookPage.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Not connected</div>
                )}
              </div>
            </div>
            {facebookConnected && tokenStatus ? (
              <span className={`px-2.5 py-1 bg-${tokenStatus.color}-100 text-${tokenStatus.color}-700 rounded-full text-xs font-medium`}>
                {tokenStatus.label}
              </span>
            ) : (
              <span className="px-2.5 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                Disconnected
              </span>
            )}
          </div>

          {/* Instagram */}
          <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${instagramConnected ? 'bg-green-100' : 'bg-gray-200'}`}>
                {instagramConnected ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-xs font-bold text-gray-500">IG</span>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">Instagram</div>
                {instagramConnected && instagramAccount ? (
                  <div className="text-sm text-gray-600">
                    @{instagramAccount.username}
                    {instagramAccount.followersCount !== null && (
                      <span className="text-gray-500 ml-2">
                        • {instagramAccount.followersCount.toLocaleString()} followers
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Not connected</div>
                )}
              </div>
            </div>
            {instagramConnected ? (
              <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                Connected
              </span>
            ) : (
              <span className="px-2.5 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                Disconnected
              </span>
            )}
          </div>
        </div>

        {/* TikTok group */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              TikTok
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleConnect("tiktok")}
                disabled={isConnecting !== null}
                className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {isConnecting === "tiktok" && <SpinnerIcon className="w-3 h-3" />}
                {tiktokConnected ? "Reconnect" : "Connect"}
              </button>
              {tiktokConnected && (
                <button
                  onClick={() => setDisconnectTarget("tiktok")}
                  className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm font-medium transition-colors"
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tiktokConnected ? 'bg-green-100' : 'bg-gray-200'}`}>
                {tiktokConnected ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="text-xs font-bold text-gray-500">TT</span>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">TikTok</div>
                {tiktokConnected && tiktokAccount ? (
                  <div className="text-sm text-gray-600">
                    {tiktokAccount.displayName || tiktokAccount.username || tiktokAccount.openId}
                    {tiktokAccount.followersCount !== null && tiktokAccount.followersCount !== undefined && (
                      <span className="text-gray-500 ml-2">
                        {tiktokAccount.followersCount.toLocaleString()} followers
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Not connected</div>
                )}
              </div>
            </div>
            {tiktokConnected ? (
              <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                Connected
              </span>
            ) : (
              <span className="px-2.5 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                Disconnected
              </span>
            )}
          </div>

          {/* Auto-publish add-on: scheduled reels fan out to TikTok with the
              client's standing publish options (public, interactions allowed).
              Without it, TikTok is manual-publish-only via the consent screen. */}
          {tiktokConnected && (
            <div className="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
              <div>
                <div className="font-medium text-gray-900">Auto-publish</div>
                <div className="text-sm text-gray-500">
                  Scheduled reels post to TikTok automatically — public, comments allowed
                </div>
              </div>
              <button
                role="switch"
                aria-checked={autopublishEnabled}
                onClick={handleToggleAutopublish}
                disabled={isSavingAutopublish}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  autopublishEnabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autopublishEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      </div>

      {!facebookConnected && !instagramConnected && !tiktokConnected && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Client has not connected any social media accounts yet.
        </div>
      )}

      <ConfirmDialog
        isOpen={disconnectTarget !== null}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={handleDisconnect}
        title={confirmConfig?.title ?? ""}
        message={confirmConfig?.message(client.clientName) ?? ""}
        confirmText="Disconnect"
        variant="danger"
        isLoading={isDisconnecting}
      />
    </div>
  );
};
